// src/hooks/useConveStream.ts

import { useState, useRef, useCallback } from 'react';

const BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:80/api/conve'
  : '/api/conve';

export const useConveStream = () => {
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);

  const sendMessage = useCallback(async (
    roomId: number,
    message: string,
    model: string,
    files: File[],
    onChunk: (text: string) => void,
    onDone: (messageId: number) => void,
    onError: (msg: string) => void,
    onStatus?: (status: string) => void,
  ) => {
    setStreaming(true);

    const token = localStorage.getItem('conve_access_token');
    const formData = new FormData();
    formData.append('message', message);
    formData.append('model', model);
    files.forEach(f => formData.append('files', f));

    let aborted = false;
    const controller = new AbortController();
    abortRef.current = () => {
      aborted = true;
      controller.abort();
    };

    try {
      const response = await fetch(
        `${BASE_URL}/rooms/${roomId}/stream`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: controller.signal,
        }
      );

      if (!response.ok) throw new Error('스트리밍 요청 실패');

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done || aborted) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();

          if (trimmed.startsWith('event:')) {
            lastEvent = trimmed.slice(6).trim();
            continue;
          }

          if (trimmed.startsWith('data:')) {
            const data = trimmed.slice(5).trim();

            if (lastEvent === 'chunk') {
              const text = data
                .replace(/\\n/g, '\n')
                .replace(/\\t/g, '\t')
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, '\\');
              onChunk(text);
            } else if (lastEvent === 'done') {
              try {
                const parsed = JSON.parse(data);
                onDone(parsed.messageId);
              } catch {
                onDone(0);
              }
            } else if (lastEvent === 'error') {
              onError(data);
            } else if (lastEvent === 'status') {
              onStatus?.(data);
            } else if (lastEvent === 'ping') {
              // keepAlive 핑 - 무시
            }

            lastEvent = ''; // 항상 초기화
          }
        }
      }
    } catch (e: any) {
      if (!aborted) onError('연결이 끊어졌습니다.');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (abortRef.current) abortRef.current();
  }, []);

  return { streaming, sendMessage, stopStream };
};